import { useHomePath } from "@@/backoffice/for-providers/settings/settings/use-home-path";
import { useOptionalGroups } from "@@/optional-groups/state/optional-group-fetchers";
import {
    BuyableDetails_Description,
    BuyableDetailsV2,
} from "@@/products/buyables/buyable-details_v2";
import { ProductDetailsContextProvider } from "@@/products/product-details-context";
import { HoveringProductPageToolbar_Content_Desktop } from "@@/products/shared/product-page-toolbar";
import { useCalculatedProviderStatus } from "@@/providers/calculate-provider-status";
import { _ProductImages } from "@@/routes/products/$productId/_bookable/generic/_product_images";
import { browserLogger } from "@@/settings";
import { AddToCartWithQuantityV2 } from "@@/shared/buttons/add-to-cart-with-quantity-picker/add-to-cart-with-quantityV2";
import { FadedBottomContainer } from "@@/shared/buttons/faded-bottom-container";
import { Conditional } from "@@/shared/conditional";
import { HorizontalLine, VerticalDivider } from "@@/shared/dividers";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { ImageV2 } from "@@/shared/pictures/image";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { useNavigateTo } from "@@/shared/text/navigate-to";
import { useTheme } from "@emotion/react";
import type {
    BookableOptionId,
    ExtrasGroup,
    MinMaxRange,
    OrderItemId,
    Product,
    Provider,
} from "@towni/common";
import {
    isAlfredItemActive,
    joinTranslatables,
    providerIdZodSchema,
} from "@towni/common";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

type ExtrasProps = {
    readonly extras: ExtrasGroup;
    readonly parentOrderItemId?: OrderItemId;
    readonly bookableOptionId?: BookableOptionId;
    readonly disabled: boolean;
    readonly pickedAmount: number;
    readonly minMax: MinMaxRange;
};

const InnerProductBuyablePageV2 = (props: {
    provider: Provider;
    product: Product;
    hideLinkToShop: boolean;
    pageAnimationDisabled?: boolean;
    goBack?: () => void;
    extras?: ExtrasProps;
}) => {
    const theme = useTheme();
    const { provider, product } = props;
    const navigateTo = useNavigateTo();
    const [optionalGroups] = useOptionalGroups(product.optionalGroupIds);
    const [searchParams] = useSearchParams();

    const referrer = searchParams.get("ref");
    const referredFromProviderId = providerIdZodSchema.safeParse(
        searchParams.get("refPID"),
    );

    const providerHomePath = useHomePath(provider);
    const afterAddToCartPath = useMemo(() => {
        if (
            referredFromProviderId.success &&
            referredFromProviderId.data !== provider._id
        ) {
            // Referred from another providers page, or from unknown
            // Try to find the products providers default page
            return providerHomePath + "#goback";
        }
        if (referrer) {
            // Referred from another page
            return referrer;
        }

        return providerHomePath + "#goback";

        // return (provider?.slug ? `/shop/${provider.slug}` : "/") + "#goback";
    }, [provider, providerHomePath, referrer, referredFromProviderId]);

    const isMedium = usePageBreakpoint({ when: "M" });

    const isMobile = usePageBreakpoint({ when: "📱" });

    const providerStatus = useCalculatedProviderStatus(provider);

    const productDisabled =
        providerStatus === "PAUSED" ||
        (product.status && !isAlfredItemActive(product.status));

    const oneColumn = true; //isMobile || isMediumSmall;
    const isMobileOrMedium = isMobile || isMedium;

    const afterAddNavigation = () => {
        // setAddToCartButtonsHidden(true);
        if (props.goBack) {
            // e.g. used in booking wizard
            // to close modal instead of navigating to another page
            props.goBack();
            return;
        }
        navigateTo(afterAddToCartPath);
    };
    browserLogger.info({ isMobile });
    return (
        <ProductDetailsContextProvider
            product={product}
            provider={provider}
            extras={props.extras}>
            <LayoutGrid
                css={{
                    label: "layout-grid",
                    marginTop: isMobileOrMedium ? 0 : 20,
                    marginBottom: isMobileOrMedium ? 0 : 100,

                    flex: 1,
                    backgroundColor: theme.colors.white.asString,
                    borderRadius: isMobileOrMedium ? 0 : 20,
                    gridTemplateColumns: oneColumn
                        ? "1fr"
                        : "minmax(0, 1fr) 50px minmax(0, 1fr)",
                    gridTemplateRows: "1fr",
                    gridAutoRows: "min-content",
                    paddingBottom: isMobileOrMedium ? 0 : 20, //
                    opacity: productDisabled ? 0.8 : 1,
                    filter: productDisabled ? "grayscale(100%)" : "unset",
                    pointerEvents: productDisabled ? "none" : "unset",

                    position: "relative",

                    //  overflow: "clip",
                }}>
                <Conditional when={isMobile}>
                    {/* HEADER */}
                    <LayoutCell
                        css={{
                            padding: 20,
                            gridRow: 1,
                            label: "back_button_area",
                            display: "flex",
                            justifyContent: "space-between",
                            zIndex: 9999,
                            borderBottom: "none",
                            gridColumn: "1 / -1",
                            alignItems: "center",
                            position: "sticky",
                            top: 0,
                            alignSelf: "start",
                            backgroundColor: theme.colors.transparent.asString,
                        }}>
                        <HoveringProductPageToolbar_Content_Desktop
                            provider={provider}
                        />
                    </LayoutCell>
                </Conditional>
                <Conditional when={!isMobile}>
                    <LayoutCell
                        css={{
                            label: "images_area",
                            display: "flex",
                            justifyContent: "stretch",
                            alignTracks: "stretch",
                            gridColumn: "1 / -1",
                            gridRow: 1,
                            paddingTop: 0,
                            paddingLeft: 0,
                            paddingRight: 0,
                            position: "relative",
                            height: "fit-content",
                            overflow: "hidden",
                            backgroundColor:
                                theme.colors.default.background.asString,
                            borderRadius: isMobile ? 0 : 10,
                            ...(!isMobile
                                ? {
                                      marginTop: (isMedium ? 27 : 54) / 2,
                                      marginLeft: (isMedium ? 30 : 60) / 2,
                                      marginRight: (isMedium ? 30 : 60) / 2,
                                      marginBottom: (isMedium ? 30 : 40) / 2,
                                  }
                                : {}),
                        }}>
                        <_ProductImages
                            product={product}
                            provider={provider}
                            fallbackOnProvider
                        />
                        <div
                            css={{
                                position: "absolute",
                                bottom: 20,
                                left: 20,
                                zIndex: 100,
                                label: "LOGO",
                            }}>
                            <ImageV2
                                imageSource={provider.logoImageRef}
                                title={joinTranslatables([
                                    provider.meta.title,
                                    " - Logo",
                                ])}
                                css={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 3000,
                                    overflow: "hidden",
                                }}
                            />
                        </div>
                    </LayoutCell>
                </Conditional>
                <Conditional when={!oneColumn}>
                    {/* COLUMN_ONE */}
                    <LayoutCell
                        css={{
                            gridColumn: 1,
                            label: "column_one",
                            paddingTop: 20,
                            paddingLeft: 20,
                            paddingRight: 20,
                            backgroundColor:
                                theme.colors.default.background.asString,
                            ...(!isMobile
                                ? {
                                      paddingLeft: isMedium ? 15 : 30,
                                      paddingRight: oneColumn
                                          ? isMedium
                                              ? 15
                                              : 30
                                          : 0,

                                      paddingTop: 0,
                                  }
                                : {}),
                        }}>
                        <BuyableDetails_Description
                            provider={provider}
                            product={product}
                            hideLinkToShop={props.hideLinkToShop}
                            hideImage={!oneColumn}
                        />

                        <Conditional when={oneColumn}>
                            <VerticalDivider L />
                            <HorizontalLine fillParentWidth />
                            <VerticalDivider S />
                        </Conditional>
                    </LayoutCell>
                </Conditional>
                <Conditional when={!oneColumn}>
                    {/* SPACER */}
                    <LayoutCell
                        css={{
                            label: "spacer",
                            gridColumn: 2,
                            backgroundColor:
                                theme.colors.default.background.asString,
                        }}
                    />
                </Conditional>

                {/* COLUMN_TWO */}
                <LayoutCell
                    css={{
                        label: "buyable_area",
                        gridColumn: 1,

                        paddingLeft: isMobile ? 0 : 20,
                        paddingRight: isMobile ? 0 : 20,
                        //paddingBottom: isMobile ? 0 : 20,
                        //    height: "100%",
                        paddingTop: 0, //isMobile ? 0 : 20,
                        backgroundColor:
                            theme.colors.default.background.asString,
                        ...(!isMobile
                            ? {
                                  gridColumn: oneColumn ? 1 : 3,
                                  //gridRow: oneColumn ? 1 : 2,
                                  paddingLeft: oneColumn
                                      ? isMedium
                                          ? 15
                                          : 30
                                      : 0,
                                  paddingRight: isMedium ? 15 : 30,
                              }
                            : {}),
                    }}>
                    <Conditional when={oneColumn}>
                        <BuyableDetails_Description
                            provider={provider}
                            product={product}
                            hideLinkToShop={props.hideLinkToShop}
                            hideImage={!isMobile}
                        />
                    </Conditional>

                    <BuyableDetailsV2
                        provider={provider}
                        product={product}
                        optionalGroups={optionalGroups}
                    />

                    <FadedBottomContainer
                        position="sticky"
                        padding={{ leftRight: 20 }}>
                        <AddToCartWithQuantityV2
                            afterAddNavigation={afterAddNavigation}
                            clearBeforeAdding={false}
                            pickLimit={
                                props.extras
                                    ? {
                                          minMax: props.extras.minMax,
                                          alreadyPicked:
                                              props.extras.pickedAmount,
                                      }
                                    : undefined
                            }
                            padding={{
                                bottom: 20,
                            }}
                        />
                    </FadedBottomContainer>
                </LayoutCell>
            </LayoutGrid>
        </ProductDetailsContextProvider>
    );
};

export { InnerProductBuyablePageV2 };
export type { ExtrasProps };
export default InnerProductBuyablePageV2;
